import React, { createContext, useCallback, useState, useContext } from 'react';
import { CognitoUserPool, AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { configContext } from './config';
import authService from '../services/authService';

export type UserSession = { 
  accessToken: string; 
  idToken: string; 
}

interface LoginFunction {
  ( username: string, 
    password: string, 
    options?: {
    onSuccess?: (session: UserSession) => void;
    onFailure?: (err: any) => void;
    newPasswordRequired?: (userAttributes: any, requiredAttributes: any) => void;
    isAdmin?: boolean;
  }) : void;
};

interface LogoutFunction {
  (): void;
}

export type UserContextType = {
  isLoggedIn: boolean;
  accessToken: string | null;
  login: LoginFunction;
  logout: LogoutFunction;
};

const userContext = createContext<UserContextType>({ 
  isLoggedIn: false,
  accessToken: null,
  login: (...args) => { 
    console.error('Login function not implemented', args);
    return 
  },
  logout: () => {}
});

const UserContextProvider: React.FC<{ 
  children: React.ReactNode 
}> = ({ children }) => {
  
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string|null>(null);

  const config = useContext(configContext);

  const login: LoginFunction = useCallback((username, password, options) => {
    if (!config) { 
      console.error('Config not available');
      return;
    }

    if (!config.cognito) {
      console.error('Cognito config not available');
      return;
    }

    // [PERFORMANCE] Move Cognito UserPool creation outside of the login function?
    // #region Create User Pool
    // Use the single config - domain determines which pool we're using
    const cognitoClientKey = config.cognito.clientId;
    const cognitoUserpool = config.cognito.userPoolId;

    const poolData = {
      UserPoolId: cognitoUserpool,
      ClientId: cognitoClientKey,
      endpoint: import.meta.env.VITE_COGNITO_ENDPOINT // Yes, this is lowercase unlike ALL the others
      //endpoint: "/cognito"
    };

    console.log('Cognito User Pool Data:', poolData);

    const UserPool = new CognitoUserPool(poolData);
    // #endregion

    // Create Auth Creds
    const authenticationData = {
        Username: username,
        Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    // Create Cognito User
    const userData = {
        Username: username,
        Pool: UserPool,
    };

    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result: CognitoUserSession) => {
          const token = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();

          console.log('Access Token:', token);
          console.log('ID Token:', idToken);

          setAccessToken(token);
          setIsLoggedIn(true);
          
          // Store tokens using authService (sessionStorage)
          authService.setupUserPool({
            userPoolId: cognitoUserpool,
            clientId: cognitoClientKey,
            endpoint: import.meta.env.VITL_COGNITO_ENDPOINT
          });
          authService.storeTokens(result, username);

          options?.onSuccess && options.onSuccess({
              accessToken: token,
              idToken: idToken
          });
      },

      onFailure: (err: Error) => {
          console.error('Login failed:', err);
          setIsLoggedIn(false);
          setAccessToken(null);
          options?.onFailure && options.onFailure(err);
      },

      newPasswordRequired: options?.newPasswordRequired || undefined
    });

  }, [config]);

  const logout: LogoutFunction = useCallback(() => {
    if (!config) {
      console.error('Config not available');
      return;
    }

    if (!config.cognito) {
      console.error('Cognito config not available');
      return;
    }

    const poolData = {
      UserPoolId: config.cognito.userPoolId,
      ClientId: config.cognito.clientId,
    };

    const UserPool = new CognitoUserPool(poolData);
    const cognitoUser = UserPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
      setIsLoggedIn(false);
      setAccessToken(null);
      
      // Clear tokens using authService
      authService.logout();
    }
  }, [config]);

  return (
    <userContext.Provider value={{
      isLoggedIn: isLoggedIn,
      accessToken: accessToken,
      login: login,
      logout: logout
    }}>
      {children}
    </userContext.Provider>
  );
};

export { 
  UserContextProvider, UserContextProvider as default,
  userContext
};